import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { EVENT } from '@/constants/events'
import {
  CALENDAR_ITEM_TYPE,
  RESOURCE_ALLOCATION_HIGHLIGHT_REASON,
  type PackingContainerId,
} from '@/constants/persoplan'
import EventHub from '@/eventHub'
import {
  useJumpTargetRegistry,
  type JumpTargetBase,
} from '@/composition/useJumpTargetRegistry'
import {
  useCalendarJumpRequest,
  type CalendarJumpTargetType,
} from '@/composition/useCalendarJump'

type CalendarJumpControllerTarget = {
  type: CalendarJumpTargetType
  id: string | number
}

const packingContainerIdByTargetType: Record<
  CalendarJumpTargetType,
  PackingContainerId
> = {
  job: 'CalendarJobs',
  project: 'CalendarJobs',
  resourceAllocation: 'CalendarJobs',
  stockTypeService: 'CalendarOperations',
  purchaseOrder: 'CalendarOperations',
}

export function useCalendarJumpController() {
  const store = useStore()
  const jumpTargetRegistry = useJumpTargetRegistry()
  const calendarJumpRequest = useCalendarJumpRequest()
  const unavailableTarget = ref<CalendarJumpControllerTarget>()
  /** Prevents multiple executions of the same jump */
  const lastExecutedRequestId = ref<number>()

  const currentTarget = computed(() => {
    const request = calendarJumpRequest.currentJumpRequest.value
    if (!request) return

    return {
      ...request,
      id: request.id,
    }
  })

  function mapToJumpTarget(
    target: CalendarJumpControllerTarget
  ): JumpTargetBase {
    return {
      type: target.type,
      id: target.id,
    }
  }

  function executeJump() {
    const target = currentTarget.value
    if (!target) return

    if (lastExecutedRequestId.value === target.requestId) return

    const jumpTarget = mapToJumpTarget(target)
    const isAvailable = jumpTargetRegistry.isAvailable(jumpTarget)
    if (!isAvailable) return

    unavailableTarget.value = undefined
    highlight(target)
    jumpTargetRegistry.jumpTo(jumpTarget)
    lastExecutedRequestId.value = target.requestId
  }

  function markUnavailableIfCurrentTargetIsMissing() {
    const target = currentTarget.value
    if (!target) return

    if (lastExecutedRequestId.value === target.requestId) return

    const jumpTarget = mapToJumpTarget(target)
    const isAvailable = jumpTargetRegistry.isAvailable(jumpTarget)

    if (isAvailable) {
      executeJump()
      return
    }

    unavailableTarget.value = target
  }

  async function markUnavailableIfCurrentTargetIsMissingAfterRender() {
    await nextTick()

    markUnavailableIfCurrentTargetIsMissing()
  }

  function onPackingFinished({ id }: { id: PackingContainerId }) {
    const target = currentTarget.value
    if (!target) return

    const packingContainerId = packingContainerIdByTargetType[target.type]

    if (id !== packingContainerId) return

    executeJump()
    markUnavailableIfCurrentTargetIsMissingAfterPacking()
  }

  async function markUnavailableIfCurrentTargetIsMissingAfterPacking() {
    // Calendar items register their jump targets after packing has settled.
    await new Promise((resolve) => setTimeout(resolve, 200))

    markUnavailableIfCurrentTargetIsMissingAfterRender()
  }

  function highlight(target: CalendarJumpControllerTarget) {
    if (target.type === 'resourceAllocation') {
      EventHub.$emit(EVENT.RESOURCE_ALLOCATION_CLEAR_HIGHLIGHT, {
        reason: RESOURCE_ALLOCATION_HIGHLIGHT_REASON.SCROLL_INTO_VIEW,
      })
      EventHub.$emit(EVENT.RESOURCE_ALLOCATION_HIGHLIGHT, {
        id: Number(target.id),
        reason: RESOURCE_ALLOCATION_HIGHLIGHT_REASON.SCROLL_INTO_VIEW,
      })
    }

    if (target.type === 'job') {
      store.commit('persoplan/clearHighlightedItems')
      store.commit('persoplan/addToHighlightedItems', {
        id: Number(target.id),
        type: CALENDAR_ITEM_TYPE.JOB,
      })
    }

    if (target.type === 'project') {
      store.commit('persoplan/clearHighlightedItems')
      store.commit('persoplan/addToHighlightedItems', {
        id: Number(target.id),
        type: CALENDAR_ITEM_TYPE.PROJECT,
      })
    }

    if (target.type === 'stockTypeService') {
      store.commit('persoplan/clearHighlightedItems')
      store.commit('persoplan/addToHighlightedItems', {
        id: Number(target.id),
        type: CALENDAR_ITEM_TYPE.STOCK_TYPE_SERVICE,
      })
    }

    if (target.type === 'purchaseOrder') {
      store.commit('persoplan/clearHighlightedItems')
      store.commit('persoplan/addToHighlightedItems', {
        id: Number(target.id),
        type: CALENDAR_ITEM_TYPE.PURCHASE_ORDER,
      })
    }
  }

  function closeUnavailableNotification() {
    unavailableTarget.value = undefined
  }

  watch(
    calendarJumpRequest.currentJumpRequest,
    () => {
      unavailableTarget.value = undefined
      executeJump()

      // Wait for the relevant calendar packing event before deciding that a
      // target is unavailable. Calendar items register only after packing.
    },
    { immediate: true }
  )

  watch(jumpTargetRegistry.registeredTargetsVersion, () => {
    executeJump()
  })

  EventHub.$on(EVENT.PACKING_FINISHED, onPackingFinished)

  onBeforeUnmount(() => {
    EventHub.$off(EVENT.PACKING_FINISHED, onPackingFinished)
  })

  return {
    unavailableTarget,
    closeUnavailableNotification,
  }
}
